import React, { useState, useEffect, useCallback } from "react";
import { PiBedBold } from "react-icons/pi";
import { BiBath } from "react-icons/bi";
import { RxCrop } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion'



import PropTypes from 'prop-types';
import "./ConstructionCard.css";

// SVG icons as components (keep all the same)
const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);


const ConstructionCard = ({ data }) => {
//   console.log("ConstructionCard received data:", {
//     title: data?.title,
//     description: data?.description,
//     area: data?.area,
//     images: data?.images}
// )

  const [currentImg, setCurrentImg] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [previewPosition, setPreviewPosition] = useState("top");

  const navigate = useNavigate();

  const handleCardClick = () => { 
    navigate(`/properties/${data.id}`, { state: { propertyData: data } });
  };

const CLOUD_NAME = "dggey8rb6";

const images = Array.isArray(data?.images) && data.images.length > 0
  ? data.images.map((img) =>
      img.startsWith("http")
        ? img
        : `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/w_800,h_500,c_fill/${img}`
    )
  : ["<div styles = {{backgroundPosition: center}}>/images/logo.png</div>"];



  // Determine if preview should be on top or bottom
  useEffect(() => {
    const calculatePosition = () => {
      const cardEl = document.querySelector(".construction-card");
      if (!cardEl) return "top";
      
      const rect = cardEl.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      if (spaceBelow > spaceAbove || spaceAbove < 120) {
        return "bottom";
      }
      return "top";
    };
    
    if (showPreview) {
      setPreviewPosition(calculatePosition());
    }
  }, [showPreview]);

  // Memoize image navigation functions
  const nextImage = useCallback(() => {
    setCurrentImg((prev) => (prev + 1) % images.length);
    setImageError(false);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImg((prev) => (prev - 1 + images.length) % images.length);
    setImageError(false);
  }, [images.length]);

  // Handle image errors
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);


    const displayArea = () => {
    if (!data?.area) return 'N/A';
    
    // Handle both old and new area formats
    if (typeof data.area === 'object' && data.area !== null) {
      // New format: { value: number, unit: string }
      return `${data.area.value || 'N/A'} ${data.area.unit || 'sqft'}`;
    } else {
      // Old format: number or string (backward compatibility)
      return `${data.area} sqft`;
    }
  };

  const openGmail = (email) => {
    if (!email) {
      alert("❌ No email address provided.");
      return;
    }

    const title = data?.title || 'N/A';
    const price = data?.price ? `Rs ${data.price.toLocaleString()}` : 'N/A';
    
    // Update area display in email
    const area = displayArea();
    
    const location = [
      data?.location?.address,
      data?.location?.city,
      data?.location?.country,
    ]
      .filter(Boolean)
      .join(', ') || 'N/A';

    const subject = encodeURIComponent(`Inquiry about ${title} from Gladious`);
    const body = encodeURIComponent(
      `Hello,\n\nI am interested in the following property:\n\n` +
      `Title: ${title}\n` +
      `Price: ${price}\n` +
      `Area: ${area}\n` +
      `Location: ${location}\n\n` +
      `Please provide more details.\n\nThank you.`
    );

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    } else {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
      window.open(gmailUrl, '_blank');
    }
  };




  // Auto-rotate images
  useEffect(() => {
    if (!isImageHovered && !showPreview && images.length > 1) {
      const timer = setTimeout(nextImage, 4000);
      return () => clearTimeout(timer);
    }
  }, [currentImg, isImageHovered, images.length, nextImage, showPreview]);

  if (!data) return <div className="card-error">No data provided</div>;

  return (
<motion.div
  className="card-wrapper"
  initial={{ opacity: 0, y: 50 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.2 }}
  transition={{ duration: 0.6, ease: "easeOut" }}
>      <div className="construction-card" onClick={handleCardClick}>
        {/* Image Slider */}

<div className="construction-card-header">

            <span className="category-text">
              {data.category || "Property"}
            </span>

            <h2 className="property-title">
              {data.title || "No Title Provided"}
            </h2>
          
          
          <p className="property-location">
            {data.location?.city || "Unknown"}, {data.location?.country || "Unknown"}
          </p>
</div>
        <div 
          className="image-slider"
          onMouseEnter={() => setIsImageHovered(true)}
          onMouseLeave={() => setIsImageHovered(false)}
        >
          <div className="slider-image-container">
            {images.map((img, idx) => (
              <img 
                key={idx}
                src={imageError ? "/images/logo.png" : img}
                alt={`Property ${idx + 1}`}
                className={`slider-image ${currentImg === idx ? 'active' : ''}`}
                onError={handleImageError}
                loading="lazy"
              />
            ))}
          </div>
                    
          {(isImageHovered && images.length>1 ) &&(
            <>
              <button 
                className="nav-button prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="Previous image"
              >
                <ChevronLeftIcon />
              </button>
              <button 
                className="nav-button next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="Next image"
              >
                <ChevronRightIcon />
              </button>
            </>
          )}
          
          {showPreview && (
            <div className={`preview-box ${previewPosition === "top" ? "preview-box-top" : "preview-box-bottom"}`}>
              <div className="preview-header">
                <span className="preview-title">All Images</span>
                <button 
                  className="preview-close-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPreview(false);
                  }}
                  aria-label="Close preview"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="preview-images-container">
                {images.map((img, index) => (
                  <div 
                    key={index}
                    className={`preview-image-wrapper ${currentImg === index ? 'active' : ''}`}
                  >
                    <img 
                      src={img} 
                      alt={`preview-${index}`} 
                      className="preview-image"
                      onClick={() => {
                        setCurrentImg(index);
                        setShowPreview(false);
                      }}
                      onError={handleImageError}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        

        <div className="card-content">
                    <p className="property-price">
            Rs {data.price?.toLocaleString() || 0}
          </p>
          
          <div className="property-features">
            <div className="feature-item">
<PiBedBold className="feature-icon" />
              <span className="feature-text">{data.bedrooms || 0} Beds</span>
            </div>
            <div className="feature-item">
<BiBath className="feature-icon"/>
    <span className="feature-text">{data.bathrooms || 0} Baths</span>
            </div>
            {/* Updated area display */}
            <div className="feature-item">
<RxCrop className="feature-icon"/>
              <span className="feature-text">{displayArea()}</span>
            </div>
          </div>


{/* Descriptiion */}
<p className="property-description">
  {data?.description || "No description available"}
</p>

{/* Action Buttons */}
    <div className="action-buttons-container">
<button className="sv-action-button"><a href="/contact">Schedule Viewing</a></button>

      <div className="action-buttons">
  <a 
    href={`tel:${data.phone || ''}`} 
    className="call-button"
  >
    <span className="construction-card-button-icon"><PhoneIcon /></span>
  </a>


  <button 
    onClick={() => openGmail(data.email)} 
    className="email-button"
  >
    <span className="construction-card-button-icon"><EnvelopeIcon /></span>
  </button>
  </div>
</div>

      </div>
      </div>
    </motion.div>
  );
};

ConstructionCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number,
    area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Updated to accept both string and number
    category: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string
    }),
    images: PropTypes.arrayOf(PropTypes.string),
    phone: PropTypes.string,
    email: PropTypes.string,
    bedrooms: PropTypes.number,
    bathrooms: PropTypes.number
  })
};


export default ConstructionCard;